import React, { useEffect, useState } from "react"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Badge,
    Input,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Label,
    FormFeedback,
} from "reactstrap"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"

import { setBreadcrumbItems, addNewPayment } from "../../store/actions"
import { getPayments, getInvoices } from "../../helpers/fakebackend_helper"

const PaymentsList = props => {
    document.title = "Paiements | CRM Entreprise"

    const [payments, setPayments] = useState([])
    const [invoices, setInvoices] = useState([])
    const [loading, setLoading] = useState(true)
    const [modal, setModal] = useState(false)

    const breadcrumbItems = [
        { title: "Finances", link: "#" },
        { title: "Paiements", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Historique des Paiements", breadcrumbItems)
        fetchPayments()
        fetchInvoices()
    }, [])

    const fetchPayments = async () => {
        try {
            const response = await getPayments()
            setPayments(response.data || response)
            setLoading(false)
        } catch (error) {
            console.error('Error fetching payments:', error)
            setLoading(false)
        }
    }

    const fetchInvoices = async () => {
        try {
            const response = await getInvoices()
            const unpaidInvoices = (response.data || response).filter(
                inv => inv.balance > 0
            )
            setInvoices(unpaidInvoices)
        } catch (error) {
            console.error('Error fetching invoices:', error)
        }
    }

    const toggle = () => {
        setModal(!modal)
        if (modal) {
            validation.resetForm()
        }
    }

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            invoice_id: "",
            payment_date: new Date().toISOString().split('T')[0],
            amount: "",
            method: "bank_transfer",
            reference: "",
            notes: "",
        },
        validationSchema: Yup.object({
            invoice_id: Yup.string().required("Facture requise"),
            payment_date: Yup.date().required("Date requise"),
            amount: Yup.number()
                .required("Montant requis")
                .positive("Le montant doit être positif")
                .test('max-balance', 'Le montant dépasse le solde de la facture', function (value) {
                    const invoiceId = this.parent.invoice_id
                    const invoice = invoices.find(inv => inv.id.toString() === invoiceId)
                    if (!invoice) return true
                    return value <= invoice.balance
                }),
            method: Yup.string().required("Méthode requise"),
        }),
        onSubmit: async (values) => {
            try {
                await props.addNewPayment(values)
                toggle()
                fetchPayments()
                validation.resetForm()
            } catch (error) {
                console.error('Error adding payment:', error)
            }
        },
    })

    const selectedInvoice = invoices.find(
        inv => inv.id.toString() === validation.values.invoice_id
    )

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD'
        }).format(amount)
    }

    const paymentMethods = {
        'cash': { label: 'Espèces', color: 'success', icon: 'mdi-cash' },
        'bank_transfer': { label: 'Virement', color: 'primary', icon: 'mdi-bank' },
        'check': { label: 'Chèque', color: 'info', icon: 'mdi-checkbook' },
        'card': { label: 'Carte', color: 'warning', icon: 'mdi-credit-card' },
        'other': { label: 'Autre', color: 'dark', icon: 'mdi-cash-multiple' },
    }

    return (
        <React.Fragment>
            <div className="container-fluid">
                {/* Statistics Cards - Top */}
                {payments && payments.length > 0 && (
                    <Row className="mb-3">
                        <Col xl={3} md={6}>
                            <Card className="card-animate border-0 shadow-sm">
                                <CardBody>
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <p className="text-uppercase fw-medium text-muted mb-0">
                                                Total Encaissé
                                            </p>
                                        </div>
                                        <div className="flex-shrink-0">
                                            <h5 className="text-success mb-0">
                                                <i className="mdi mdi-trending-up align-middle"></i>
                                            </h5>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-end justify-content-between mt-3">
                                        <div>
                                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                                                {formatCurrency(payments.reduce((sum, p) => sum + parseFloat(p.amount), 0))}
                                            </h4>
                                        </div>
                                        <div className="avatar-sm flex-shrink-0">
                                            <span className="avatar-title bg-success-subtle rounded fs-3 border-0">
                                                <i className="mdi mdi-cash-multiple text-success"></i>
                                            </span>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col xl={3} md={6}>
                            <Card className="card-animate border-0 shadow-sm">
                                <CardBody>
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <p className="text-uppercase fw-medium text-muted mb-0">
                                                Nombre de Paiements
                                            </p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-end justify-content-between mt-3">
                                        <div>
                                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                                                {payments.length}
                                            </h4>
                                        </div>
                                        <div className="avatar-sm flex-shrink-0">
                                            <span className="avatar-title bg-info-subtle rounded fs-3 border-0">
                                                <i className="mdi mdi-receipt text-info"></i>
                                            </span>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col xl={3} md={6}>
                            <Card className="card-animate border-0 shadow-sm">
                                <CardBody>
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <p className="text-uppercase fw-medium text-muted mb-0">
                                                Factures Impayées
                                            </p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-end justify-content-between mt-3">
                                        <div>
                                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                                                {invoices.length}
                                            </h4>
                                        </div>
                                        <div className="avatar-sm flex-shrink-0">
                                            <span className="avatar-title bg-warning-subtle rounded fs-3 border-0">
                                                <i className="mdi mdi-alert-circle text-warning"></i>
                                            </span>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col xl={3} md={6}>
                            <Card className="card-animate border-0 shadow-sm">
                                <CardBody>
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <p className="text-uppercase fw-medium text-muted mb-0">
                                                Paiement Moyen
                                            </p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-end justify-content-between mt-3">
                                        <div>
                                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                                                {formatCurrency(payments.reduce((sum, p) => sum + parseFloat(p.amount), 0) / payments.length)}
                                            </h4>
                                        </div>
                                        <div className="avatar-sm flex-shrink-0">
                                            <span className="avatar-title bg-primary-subtle rounded fs-3 border-0">
                                                <i className="mdi mdi-chart-line text-primary"></i>
                                            </span>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col className="col-12">
                        <Card>
                            <CardBody>
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <CardTitle className="h4 mb-0">Historique des Paiements Reçus</CardTitle>
                                    <div className="d-flex gap-2">
                                        <Button color="success" onClick={toggle}>
                                            <i className="mdi mdi-plus me-1"></i> Enregistrer un paiement
                                        </Button>
                                    </div>
                                </div>

                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status"></div>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-centered table-nowrap mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>N° Paiement</th>
                                                    <th>Date</th>
                                                    <th>Facture</th>
                                                    <th>Client</th>
                                                    <th>Méthode</th>
                                                    <th>Référence</th>
                                                    <th className="text-end">Montant</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {payments && payments.length > 0 ? (
                                                    payments.map((payment, key) => (
                                                        <tr key={key}>
                                                            <td><span className="fw-bold text-primary">{payment.payment_number}</span></td>
                                                            <td>{new Date(payment.payment_date).toLocaleDateString('fr-FR')}</td>
                                                            <td>
                                                                <Link to={`/finance/invoices/${payment.invoice?.id}`}>
                                                                    {payment.invoice?.invoice_number}
                                                                </Link>
                                                            </td>
                                                            <td>
                                                                <h5 className="font-size-14 mb-0">
                                                                    {payment.customer?.first_name} {payment.customer?.last_name}
                                                                </h5>
                                                                <p className="text-muted mb-0 font-size-12">
                                                                    {payment.customer?.company_name}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <Badge color={paymentMethods[payment.method]?.color}>
                                                                    <i className={`mdi ${paymentMethods[payment.method]?.icon} me-1`}></i>
                                                                    {paymentMethods[payment.method]?.label}
                                                                </Badge>
                                                            </td>
                                                            <td>{payment.reference || '-'}</td>
                                                            <td className="text-end fw-bold text-success">
                                                                {formatCurrency(payment.amount)}
                                                            </td>
                                                            <td>
                                                                <Link to={`/finance/invoices/${payment.invoice?.id}`}>
                                                                    <button className="btn btn-sm btn-soft-primary">
                                                                        <i className="mdi mdi-eye"></i>
                                                                    </button>
                                                                </Link>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4">
                                                            <i className="mdi mdi-cash-remove font-size-24 d-block mb-2 text-muted"></i>
                                                            Aucun paiement enregistré
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Payment Modal */}
                <Modal isOpen={modal} toggle={toggle} size="lg">
                    <ModalHeader toggle={toggle}>Enregistrer un paiement</ModalHeader>
                    <ModalBody>
                        <Form onSubmit={validation.handleSubmit}>
                            <div className="mb-3">
                                <Label>Facture <span className="text-danger">*</span></Label>
                                <Input
                                    name="invoice_id"
                                    type="select"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.invoice_id}
                                    invalid={validation.touched.invoice_id && validation.errors.invoice_id}
                                >
                                    <option value="">Sélectionner une facture</option>
                                    {invoices.map(inv => (
                                        <option key={inv.id} value={inv.id}>
                                            {inv.invoice_number} - {inv.customer?.first_name} {inv.customer?.last_name}
                                            {' '}(Solde: {formatCurrency(inv.balance)})
                                        </option>
                                    ))}
                                </Input>
                                {validation.touched.invoice_id && validation.errors.invoice_id && (
                                    <FormFeedback type="invalid" className="d-block">
                                        {validation.errors.invoice_id}
                                    </FormFeedback>
                                )}
                                {selectedInvoice && (
                                    <div className="mt-2 p-2 bg-light rounded">
                                        <small>
                                            <strong>Total facture:</strong> {formatCurrency(selectedInvoice.total)}<br />
                                            <strong>Déjà payé:</strong> {formatCurrency(selectedInvoice.paid_amount)}<br />
                                            <strong className="text-danger">Solde restant:</strong> {formatCurrency(selectedInvoice.balance)}
                                        </small>
                                    </div>
                                )}
                            </div>

                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <Label>Date <span className="text-danger">*</span></Label>
                                        <Input
                                            name="payment_date"
                                            type="date"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.payment_date}
                                            invalid={validation.touched.payment_date && validation.errors.payment_date}
                                        />
                                        {validation.touched.payment_date && validation.errors.payment_date && (
                                            <FormFeedback type="invalid">{validation.errors.payment_date}</FormFeedback>
                                        )}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <Label>Montant (MAD) <span className="text-danger">*</span></Label>
                                        <Input
                                            name="amount"
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.amount}
                                            invalid={validation.touched.amount && validation.errors.amount}
                                        />
                                        {validation.touched.amount && validation.errors.amount && (
                                            <FormFeedback type="invalid">{validation.errors.amount}</FormFeedback>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <Label>Méthode <span className="text-danger">*</span></Label>
                                        <Input
                                            name="method"
                                            type="select"
                                            onChange={validation.handleChange}
                                            value={validation.values.method}
                                        >
                                            <option value="bank_transfer">Virement bancaire</option>
                                            <option value="cash">Espèces</option>
                                            <option value="check">Chèque</option>
                                            <option value="card">Carte bancaire</option>
                                            <option value="other">Autre</option>
                                        </Input>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <Label>Référence</Label>
                                        <Input
                                            name="reference"
                                            type="text"
                                            placeholder="Ex: CHQ-123456"
                                            onChange={validation.handleChange}
                                            value={validation.values.reference}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <div className="mb-3">
                                <Label>Notes</Label>
                                <Input
                                    name="notes"
                                    type="textarea"
                                    rows="3"
                                    placeholder="Notes internes..."
                                    onChange={validation.handleChange}
                                    value={validation.values.notes}
                                />
                            </div>

                            <div className="text-end mt-4">
                                <Button type="button" color="secondary" className="me-2" onClick={toggle}>
                                    Annuler
                                </Button>
                                <Button type="submit" color="success">
                                    <i className="mdi mdi-content-save me-1"></i> Enregistrer
                                </Button>
                            </div>
                        </Form>
                    </ModalBody>
                </Modal>
            </div>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({})

export default connect(mapStateToProps, {
    setBreadcrumbItems,
    addNewPayment,
})(PaymentsList)
